import React, {
    ChangeEventHandler,
    FocusEvent,
    Dispatch,
    forwardRef,
    InputHTMLAttributes,
    memo,
    useCallback, useState, KeyboardEventHandler,
} from 'react';
import {useLegacyRef} from '../../../hooks/legacy-ref';
import {KeyboardTuple} from '../../../hooks/keyboard';
import iconShow from '../../../assets/images/icons/icon-show.png';
import iconHide from '../../../assets/images/icons/icon-hide.png';

import './index.scss';
import {onClickHandler} from '../../../utils/onclick';

type SettingInputComponentProps = {
    label: string;
    value: string;
    setValue: (newValue: string) => void;
    setActiveInput: Dispatch<KeyboardTuple>;
    tempValue?: string;
};

const valueConsistsOfStarts = (value: string) => value.length > 0 && (value.match(/\*/g) || []).length === value.length;

export const SettingInputComponent = memo(forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & SettingInputComponentProps>(
    function SettingInputComponent(props, ref) {
        const {label, value, setValue, setActiveInput, tempValue, type, onFocus: onFocusProp, onBlur: onBlurProp, ...restProps} = props;
        const inputRef = useLegacyRef(ref);

        const [passwordVisible, setPasswordVisible] = useState(false);

        // noinspection JSUnusedLocalSymbols
        const onFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
            if (typeof onFocusProp === 'function') {
                onFocusProp(event);
            }

            let valueLast = value;
            if (tempValue != null) {
                if (value === tempValue) {
                    valueLast = '';
                    setValue('');
                }
            }

            if (typeof setActiveInput === 'function') {
                setActiveInput([inputRef, valueLast, setValue]);
            }
        }, [onFocusProp, value, tempValue, setActiveInput, inputRef, setValue]);

        const onBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
            if (typeof onBlurProp === 'function') {
                onBlurProp(event);
            }

            if (tempValue != null && value === '') {
                setValue(tempValue);
            }
        }, [onBlurProp, setValue, tempValue, value]);

        const onChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
            setValue(e.target.value);
        }, [setValue]);

        const onKeyPress: KeyboardEventHandler<HTMLInputElement> = useCallback((e) => {
            if (type === 'number-password') {
                if (!(/\d/.test(String.fromCharCode(e.which)))) {
                    e.preventDefault();
                }
            }
        }, [type]);

        const onClick = useCallback(() => {
            setPasswordVisible(passwordVisible => !passwordVisible);
        }, []);

        const internalType = type === 'number-password' ? 'password' : type;

        return (
            <div className='line settings-input'>
                <div className='left'>
                    <span>{label}</span>
                </div>
                <div className='right'>
                    <input
                        maxLength={40}
                        onChange={onChange}
                        className='highlighted'
                        value={value}
                        ref={inputRef}
                        onKeyPress={onKeyPress}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        type={passwordVisible ? 'text' : internalType}
                        {...restProps}/>
                    {
                        renderIf(type === 'password' && !valueConsistsOfStarts(value))(() =>
                            <img
                                src={passwordVisible ? iconHide : iconShow}
                                alt=''
                                {...onClickHandler(onClick)}/>,
                        )
                    }
                </div>
            </div>
        );
    },
));
